import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceArea,
  Brush
} from 'recharts';

// Different colors for each category
const BASE_COLORS = {
  'Numeric': ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD', '#D4A5A5', '#9B59B6', '#3498DB'],
  'Japanese Era': ['#2ECC71', '#E74C3C', '#F1C40F', '#1ABC9C', '#9B59B6', '#E67E22', '#34495E', '#16A085'],
  'Range': ['#FF851B', '#7FDBFF', '#B10DC9', '#39CCCC', '#01FF70', '#FFDC00', '#FF4136', '#F012BE'],
  'Approximate': ['#19B5FE', '#F7CA18', '#F9690E', '#D2527F', '#1E824C', '#86E2D5', '#BE90D4', '#E26A6A'],
  'Unknown': ['#FF9F43', '#EE5A24', '#FFA502', '#FDA7DF', '#D980FA', '#9980FA', '#5758BB', '#4834D4'],
  'Other': ['#6C5CE7', '#A3CB38', '#FD7272', '#778BEB', '#786FA6', '#F8A5C2', '#63CDDA', '#EA8685']
};

// Function to get color based on category and index
const getColor = (category, index) => {
  const colors = BASE_COLORS[category] || BASE_COLORS['Other'];
  return colors[index % colors.length];
};

// Threshold for when to switch to decade grouping
const YEAR_THRESHOLD = 30;

const YearDistributionChart = ({ data = [] }) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredBar, setHoveredBar] = useState(null);
  const [viewMode, setViewMode] = useState('auto'); // 'auto', 'years', or 'decades'
  const [zoomDomain, setZoomDomain] = useState(null);

  if (!data.length) return <div>No year distribution data available</div>;

  // Process and group data based on view mode
  const { processedData, categoryStats, sortedCategories, shouldGroupDecades } = useMemo(() => {
    // Calculate category totals and stats
    const stats = data.reduce((acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = {
          totalCoins: 0,
          uniqueYears: new Set()
        };
      }
      acc[item.category].totalCoins += item.count;
      acc[item.category].uniqueYears.add(item.year);
      return acc;
    }, {});

    // Sort categories by total coins
    const sorted = Object.entries(stats)
      .sort(([, a], [, b]) => b.totalCoins - a.totalCoins)
      .map(([category]) => category);

    // Determine if we should group by decades
    const uniqueYears = new Set(data.map(item => item.year));
    const shouldGroup = viewMode === 'decades' || (viewMode === 'auto' && uniqueYears.size > YEAR_THRESHOLD);

    // Process data based on grouping decision
    let processed = data;
    if (shouldGroup) {
      const decadeGroups = data.reduce((acc, item) => {
        const year = parseInt(item.year);
        if (!isNaN(year)) {
          const decade = Math.floor(year / 10) * 10;
          const key = `${decade}s`;
          if (!acc[key]) {
            acc[key] = {
              year: key,
              count: 0,
              category: item.category,
              years: new Set()
            };
          }
          acc[key].count += item.count;
          acc[key].years.add(item.year);
        } else {
          // Handle non-numeric years (e.g., "Circa 1850s")
          if (!acc[item.year]) {
            acc[item.year] = {
              year: item.year,
              count: 0,
              category: item.category,
              years: new Set([item.year])
            };
          }
          acc[item.year].count += item.count;
        }
        return acc;
      }, {});
      processed = Object.values(decadeGroups);
    }

    return {
      processedData: processed,
      categoryStats: stats,
      sortedCategories: sorted,
      shouldGroupDecades: shouldGroup
    };
  }, [data, viewMode]);

  // Filter and sort data
  const { filteredData, sortedData } = useMemo(() => {
    const filtered = selectedCategory === 'all'
      ? processedData
      : processedData.filter(item => item.category === selectedCategory);

    const sorted = [...filtered].sort((a, b) => {
      // Try to sort numerically first (handle decade strings)
      const yearA = parseInt(a.year);
      const yearB = parseInt(b.year);
      if (!isNaN(yearA) && !isNaN(yearB)) return yearA - yearB;
      // Fall back to string comparison
      return a.year.localeCompare(b.year);
    });

    return { filteredData: filtered, sortedData: sorted };
  }, [processedData, selectedCategory]);

  const handleBarClick = (data) => {
    // Start with a fresh URLSearchParams that only includes non-year parameters
    const currentParams = new URLSearchParams(window.location.search);
    const newParams = new URLSearchParams();
    
    // Copy all non-year parameters
    for (const [key, value] of currentParams.entries()) {
      if (key !== 'year') {
        newParams.set(key, value);
      }
    }
    
    if (shouldGroupDecades && data.years) {
      // For decade groups, create an OR expression in parentheses
      const matchingYears = Array.from(data.years)
        .map(year => year.toString())
        .sort((a, b) => parseInt(a) - parseInt(b));
      if (matchingYears.length > 0) {
        // Format exactly like country field for FilterPopover to create multiple filters
        const yearExpression = matchingYears.length === 1 ? matchingYears[0] : `(${matchingYears.join(' OR ')})`;
        newParams.set('year', yearExpression);
      }
    } else {
      // For individual years
      newParams.set('year', data.year);
    }
    
    navigate(`/inventory?${newParams.toString()}`);
  };

  return (
    <div className="h-96 w-full p-4 bg-white rounded-lg shadow">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Year Distribution</h2>
        <div className="flex items-center gap-2">
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="auto">Auto Group</option>
            <option value="years">Show All Years</option>
            <option value="decades">Group by Decades</option>
          </select>
          <select
            id="year-category"
            name="year-category"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="all">All Categories</option>
            {sortedCategories.map(category => (
              <option key={category} value={category}>
                {category} ({categoryStats[category].totalCoins} coins, {categoryStats[category].uniqueYears.size} years)
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="h-80 relative">
        <ResponsiveContainer width="100%" height="90%">
          <BarChart
            data={sortedData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 25,
            }}
            onClick={(e) => {
              if (e && e.activePayload) {
                handleBarClick(e.activePayload[0].payload);
              }
            }}
            onMouseMove={(e) => {
              if (e && e.activePayload) {
                setHoveredBar(e.activePayload[0].payload.year);
              } else {
                setHoveredBar(null);
              }
            }}
            onMouseLeave={() => setHoveredBar(null)}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="year"
              angle={-45}
              textAnchor="end"
              height={60}
              interval={sortedData.length > 30 ? Math.ceil(sortedData.length / 30) : 0}
              tick={{ fontSize: 10 }}
            />
            <YAxis />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-white p-2 border rounded shadow-lg">
                      <p className="font-semibold">{data.year}</p>
                      <p>{data.count} coin{data.count !== 1 ? 's' : ''}</p>
                      <p className="text-sm text-gray-600">{data.category}</p>
                      {data.years && (
                        <p className="text-xs text-gray-500 mt-1">
                          Includes years: {Array.from(data.years).join(', ')}
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar
              dataKey="count"
              name={shouldGroupDecades ? "Coins per Decade" : "Coins per Year"}
            >
              {sortedData.map((entry, index) => {
                const categoryData = filteredData.filter(d => d.category === entry.category);
                const categoryIndex = categoryData.findIndex(d => d.year === entry.year);
                const color = getColor(entry.category, categoryIndex);
                return (
                  <Cell 
                    key={`cell-${index}`}
                    fill={color}
                    style={{
                      filter: hoveredBar === entry.year ? 'brightness(0.9)' : 'none',
                      transition: 'filter 0.2s'
                    }}
                  />
                );
              })}
            </Bar>
            <Brush
              dataKey="year"
              height={30}
              stroke="#8884d8"
              travellerWidth={20}
              alwaysShowText={true}
              style={{
                '& .recharts-brush-traveller, & .recharts-brush-traveller *': {
                  outline: 'none !important'
                }
              }}
              onChange={(e) => {
                if (e.startIndex !== undefined && e.endIndex !== undefined) {
                  setZoomDomain({
                    start: sortedData[e.startIndex].year,
                    end: sortedData[e.endIndex].year
                  });
                }
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default YearDistributionChart;
